import request from "@/lib/api/handler";

import { useAuth0 } from "@auth0/auth0-react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useProfileQuery } from "@/queries/user/profile";

import { QueryKeys } from "@/queries/utils";
import { Candidate } from "@/queries/candidate/candidate";
import { IExperience } from "@/redux/features/candidateProfileSlice";
import { toast } from "sonner";

export const useDeleteExperience = (
  profileId: string,
  experienceId: string,
  onSuccess?: () => void,
) => {
  const qc = useQueryClient();
  const { getAccessTokenSilently } = useAuth0();
  const { profile } = useProfileQuery();

  const { mutate, isPending, error } = useMutation({
    mutationFn: async () => {
      const token = await getAccessTokenSilently();

      return request<IExperience>({
        method: "DELETE",
        path: `/api/candidate/${profileId || "foo"}/experience/${experienceId ?? ""}`,
        token,
      });
    },
    onSuccess: () => {
      const prevCandidate = qc.getQueryData<Candidate>(
        QueryKeys.candidate(profileId),
      );

      if (prevCandidate) {
        const updatedCandidate = {
          ...prevCandidate,
        };

        const experiences = [...prevCandidate.experiences];
        const prevExperienceIdx = experiences.findIndex(
          ({ id }) => id === experienceId,
        );

        if (prevExperienceIdx !== -1) {
          experiences.splice(prevExperienceIdx, 1);
        }
        updatedCandidate.experiences = experiences;

        qc.setQueryData<Candidate>(
          QueryKeys.candidate(profileId),
          updatedCandidate,
        );
      }

      if (profile?.id === profileId) {
        const prevSelf = qc.getQueryData<Candidate>(QueryKeys.myCandidate);
        if (prevSelf) {
          const updatedCandidate = {
            ...prevSelf,
          };

          const experiences = [...prevSelf.experiences];
          const prevExperienceIdx = experiences.findIndex(
            ({ id }) => id === experienceId,
          );

          if (prevExperienceIdx !== -1) {
            experiences.splice(prevExperienceIdx, 1);
          }
          updatedCandidate.experiences = experiences;

          qc.setQueryData<Candidate>(QueryKeys.myCandidate, updatedCandidate);
        }
      }

      onSuccess?.();
    },
    onError: () => {
      toast.error("Failed to delete experience, please try again later.");
    },
  });

  return {
    deleteExperience: mutate,
    isDeletingExperience: isPending,
    deleteExperienceError: error,
  };
};
